import { apiRequest, ApiError } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

/**
 * Admin-only REST client for the WhatsApp Connections panel: per-gateway connection status and a
 * QR-based re-bind. Mirrors src/lib/api/queues.js. Every call is admin-gated server-side; the panel
 * never sends tenantId. Endpoints live under /api/admin/whatsapp.
 */

function createAuthHeaders(token) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return { Authorization: `Bearer ${normalizedToken}` };
}

/**
 * GET /api/admin/whatsapp/gateways — one row per configured business number with its effective
 * connection status. `status` is one of connected | qr | connecting | logged_out | offline |
 * unknown. `qrDataUrl` (PNG data-URL) is only present while status === "qr".
 * @returns {Promise<Array<{ botId: string, label: string, backendConnected: boolean, status: string, qrDataUrl: string, updatedAt: string }>>}
 */
export async function fetchWhatsappGateways({ token } = {}) {
  const res = await apiRequest(`/api/admin/whatsapp/gateways`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  const rows = Array.isArray(res?.gateways) ? res.gateways : [];
  return rows.map((g) => ({
    botId: String(g?.botId || "").trim(),
    label: String(g?.label || "").trim(),
    backendConnected: Boolean(g?.backendConnected),
    status: String(g?.status || "unknown").trim() || "unknown",
    qrDataUrl: typeof g?.qrDataUrl === "string" ? g.qrDataUrl : "",
    updatedAt: g?.updatedAt ? String(g.updatedAt) : "",
  }));
}

/**
 * POST /api/admin/whatsapp/gateways/{botId}/relink — ask a gateway to drop its current WhatsApp
 * session and start a fresh pairing (new QR). 409 when the gateway process is offline (unreachable).
 */
export async function relinkWhatsappGateway({ token, botId } = {}) {
  const id = String(botId || "").trim();
  if (!id) {
    throw new ApiError("botId is required", 400);
  }
  return apiRequest(`/api/admin/whatsapp/gateways/${encodeURIComponent(id)}/relink`, {
    method: "POST",
    headers: createAuthHeaders(token),
  });
}
